import { z } from 'zod';

/**
 * Кабинет руководителя и отчёты. Схемы отражают то, что реально отдаёт
 * `analytics` (services/analytics/src/modules/reports/application/reports.ts),
 * а не полный список из docs/03-modules/12-analytics.md: часть read-моделей
 * там зависит от модулей вехи 3.4/3.6/3.8, которых ещё нет.
 */

export const RevenueReport = z.object({
  total: z.number().int(),
  byMethod: z.array(z.object({ method: z.string(), amount: z.number().int() })),
  byDate: z.array(z.object({ date: z.string(), method: z.string(), amount: z.number().int() })),
});
export type RevenueReport = z.infer<typeof RevenueReport>;

export const CashFlowReport = z.object({
  partial: z.boolean(),
  series: z.array(z.object({ date: z.string(), netAmount: z.number().int(), cumulative: z.number().int() })),
  /**
   * Отток за период: закупки, выплаченный переменный ФОТ, возвраты авансов,
   * расходы (`expense`), изъятия из сейфа (`safe_withdrawal`) и прежние
   * «прочие расходы» (`other_expense`) — без оклада. Выдача из кассы в сейф
   * — перемещение внутри клиники, оттоком не считается.
   */
  outflows: z.object({
    total: z.number().int(),
    byDirection: z.array(z.object({ direction: z.string(), amount: z.number().int() })),
  }),
  /** Откуда пришло: оплаты по способам (без зачёта авансов) и внесённые авансы по способам. */
  inflows: z
    .object({
      total: z.number().int(),
      payments: z.array(z.object({ method: z.string(), amount: z.number().int() })),
      walletDeposits: z.array(z.object({ method: z.string(), amount: z.number().int() })),
    })
    .default({ total: 0, payments: [], walletDeposits: [] }),
  /** Куда ушло по видам расходов — расшифровка направления `expense` в `outflows`. */
  expenses: z
    .array(z.object({ categoryId: z.string(), name: z.string(), amount: z.number().int(), count: z.number().int() }))
    .default([]),
  /**
   * Внутри клиники — движение, которое не приход и не уход: из кассы в сейф
   * и зачёт авансов в оплату счетов (выручка, но деньги пришли раньше).
   */
  internal: z.object({ safeDeposits: z.number().int(), walletCharges: z.number().int() }).default({ safeDeposits: 0, walletCharges: 0 }),
  /** По дням: пришло и ушло. */
  days: z.array(z.object({ date: z.string(), inflow: z.number().int(), outflow: z.number().int() })).default([]),
});
export type CashFlowReport = z.infer<typeof CashFlowReport>;

export const UtilizationReport = z.union([
  z.object({ by: z.literal('chair'), items: z.array(z.object({ chairId: z.uuid(), date: z.string(), busyMinutes: z.number().int() })) }),
  z.object({ by: z.literal('doctor'), items: z.array(z.object({ doctorId: z.uuid(), month: z.string(), busyMinutes: z.number().int() })) }),
]);
export type UtilizationReport = z.infer<typeof UtilizationReport>;

export const DoctorStats = z.object({
  doctorId: z.uuid(),
  month: z.string(),
  booked: z.number().int(),
  completed: z.number().int(),
  noShow: z.number().int(),
  cancelled: z.number().int(),
  noShowRate: z.number().nullable(),
  busyMinutes: z.number().int(),
  /** Личная выручка (веха 3.9) — сумма позиций счёта, привязанных к врачу, на момент выставления. */
  revenue: z.number().int(),
});
export type DoctorStats = z.infer<typeof DoctorStats>;

export const LabPerformanceReport = z.object({
  labTenantId: z.uuid(),
  month: z.string(),
  closedCount: z.number().int(),
  onTimeCount: z.number().int(),
  remakeCount: z.number().int(),
  onTimeRate: z.number().nullable(),
  remakeRate: z.number().nullable(),
});
export type LabPerformanceReport = z.infer<typeof LabPerformanceReport>;

export const OverdueLabOrder = z.object({
  orderId: z.uuid(),
  labTenantId: z.uuid(),
  dueDate: z.iso.datetime(),
  /** «Наряд № 12 · Иванов Иван» — подставляет шлюз core-api; у аналитики самой этих слов нет. */
  label: z.string().optional(),
});
export type OverdueLabOrder = z.infer<typeof OverdueLabOrder>;

export const PlanConversionReport = z.object({
  month: z.string(),
  proposed: z.number().int(),
  agreed: z.number().int(),
  cancelled: z.number().int(),
  conversionRate: z.number().nullable(),
  tierDistribution: z.array(z.object({ tier: z.string(), count: z.number().int(), share: z.number().nullable() })),
});
export type PlanConversionReport = z.infer<typeof PlanConversionReport>;

export const AGING_BUCKETS = ['current', '1-30', '31-60', '61-90', '90+'] as const;
export const DebtsAgingReport = z.object({
  asOf: z.string(),
  buckets: z.array(z.object({ bucket: z.enum(AGING_BUCKETS), count: z.number().int(), amount: z.number().int() })),
  total: z.number().int(),
});
export type DebtsAgingReport = z.infer<typeof DebtsAgingReport>;

export const PatientSourceReport = z.object({
  sourceCode: z.string(),
  patients: z.number().int(),
  revenue: z.number().int(),
  ltv: z.number().nullable(),
});
export type PatientSourceReport = z.infer<typeof PatientSourceReport>;

export const PayrollVariableReport = z.object({ month: z.string(), amount: z.number().int(), partial: z.boolean() });
export type PayrollVariableReport = z.infer<typeof PayrollVariableReport>;

/** Сумма списаний материалов за месяц (docs/03-modules/14-inventory.md, §7). */
export const MaterialCostReport = z.object({ month: z.string(), amount: z.number().int() });
export type MaterialCostReport = z.infer<typeof MaterialCostReport>;

/**
 * Расходы клиники за месяцы периода, по видам: новые «Расходы» и прежние
 * «прочие», перенесённые в них (`category` — имя вида). Имя поля осталось от
 * вехи 3.9 ради совместимости ответа.
 */
export const ManualExpensesReport = z.object({
  month: z.string(),
  total: z.number().int(),
  byCategory: z.array(z.object({ category: z.string(), amount: z.number().int() })),
});
export type ManualExpensesReport = z.infer<typeof ManualExpensesReport>;

/**
 * Выручка − переменный ФОТ − себестоимость материалов − прочие расходы.
 * `partial: true` всегда: полного ФОТ (оклад, Staff, веха 3.6 — поле
 * зашифровано и не проецируется в аналитику) в формуле по-прежнему нет — см.
 * докстроку `readManagementProfit`.
 */
export const ManagementProfitReport = z.object({ month: z.string(), amount: z.number().int(), partial: z.boolean() });
export type ManagementProfitReport = z.infer<typeof ManagementProfitReport>;

export const AnalyticsFreshness = z.object({ asOf: z.iso.datetime().nullable() });
export type AnalyticsFreshness = z.infer<typeof AnalyticsFreshness>;

/**
 * Загрузка клиники за месяц: время проведённых приёмов к рабочему времени.
 *
 * Знаменатель собирает `core-api`, а не аналитика: смены врачей и часы
 * работы лежат в расписании, событий о них аналитика не получает — ей и не
 * нужно, это справочник, а не факт. Считается за уже прошедшую часть месяца:
 * сравнивать проведённые приёмы с ещё не наступившими сменами — занижать
 * загрузку тем сильнее, чем раньше в месяце смотришь.
 *
 * `basis` — откуда знаменатель: по сменам врачей; по часам работы и креслам,
 * если смен в этом месяце не ставили; `none` — сравнивать не с чем, и тогда
 * `rate` пуст, а не ноль: ноль на карточке читался бы как простой.
 */
export const ClinicUtilization = z.object({
  busyMinutes: z.number().int().nonnegative(),
  availableMinutes: z.number().int().nonnegative(),
  rate: z.number().nullable(),
  basis: z.enum(['shifts', 'clinic_hours', 'none']),
});
export type ClinicUtilization = z.infer<typeof ClinicUtilization>;

/**
 * Итоги предыдущего периода той же длины — для стрелок «к прошлому периоду»
 * на карточках кабинета. Только числа: детализация прошлого периода не нужна,
 * нужен знак и величина изменения.
 */
export const DashboardPrevious = z.object({
  range: z.object({ from: z.string(), to: z.string() }),
  revenueTotal: z.number().int(),
  cashFlowNet: z.number().int(),
  cashFlowOutflows: z.number().int(),
  plansProposed: z.number().int(),
  plansAgreed: z.number().int(),
  newPatients: z.number().int(),
  payrollVariable: z.number().int(),
  materialCost: z.number().int(),
  manualExpenses: z.number().int(),
  completedVisits: z.number().int(),
  noShow: z.number().int(),
  busyMinutes: z.number().int(),
});
export type DashboardPrevious = z.infer<typeof DashboardPrevious>;

export const OwnerDashboard = z.object({
  /** Период кабинета днями включительно; у ответов прежней версии пусто. */
  range: z.object({ from: z.string(), to: z.string() }).optional(),
  /** Месяцы, по которым сложены помесячные метрики (врачи, планы, источники, ФОТ). */
  months: z.array(z.string()).optional(),
  previous: DashboardPrevious.optional(),
  revenue: RevenueReport,
  cashFlow: CashFlowReport,
  doctors: z.array(DoctorStats),
  planConversion: PlanConversionReport,
  patientSources: z.array(PatientSourceReport),
  debtsAging: DebtsAgingReport,
  overdueLabOrders: z.array(OverdueLabOrder),
  payrollVariable: PayrollVariableReport,
  materialCost: MaterialCostReport,
  manualExpenses: ManualExpensesReport,
  managementProfit: ManagementProfitReport,
  freshness: AnalyticsFreshness,
  /** Добавляет шлюз `core-api`; аналитика сама этого поля не отдаёт. */
  utilization: ClinicUtilization.optional(),
});
export type OwnerDashboard = z.infer<typeof OwnerDashboard>;

/**
 * Первичный документ за показателем.
 *
 * Аналитика знает только идентификаторы: имён и медицинских подробностей она
 * не хранит вовсе. Подписи собирает `core-api` — у него есть и данные, и право
 * пользователя на них. Пока в списке стояли обрезки идентификаторов, разворот
 * показателя не отвечал ни на один вопрос: uuidv7 начинается со времени
 * создания, поэтому у пациентов, заведённых в одну секунду, первые восемь
 * знаков совпадают — двести разных карт выглядели одной строкой, повторённой
 * двести раз.
 */
export const DrillDownDocument = z.object({
  id: z.uuid(),
  /** Чем документ называют вслух: «Счёт № 000123», «Иванова Мария Петровна». */
  title: z.string().min(1),
  /** Дата, сумма, состояние — одной строкой. Пусто, когда добавить нечего. */
  detail: z.string().nullable(),
  /**
   * Пациент документа — у плана лечения адрес экрана строится через карту
   * пациента, а не через сам план; у остальных видов пусто.
   */
  patientId: z.uuid().nullable().optional(),
});
export type DrillDownDocument = z.infer<typeof DrillDownDocument>;

export const DrillDownResult = z.object({
  entity: z.string(),
  documents: z.array(DrillDownDocument),
  /**
   * Сколько документов стоит за показателем. Список короче: месяц крупной
   * клиники — это тысячи строк, а вопрос «что за этой цифрой» решается на
   * первых из них. Разница между `total` и длиной списка показывается прямо в
   * окне, иначе усечение читалось бы как расхождение с самим показателем.
   */
  total: z.number().int().nonnegative(),
});
export type DrillDownResult = z.infer<typeof DrillDownResult>;

/**
 * Отчёты с выгрузкой в XLSX — все, что есть на экране отчётов: у каждого
 * пункта переключателя своя кнопка «Скачать», а не у пяти из восьми.
 */
export const EXPORTABLE_REPORTS = [
  'revenue',
  'cash-flow',
  'doctors',
  'plan-conversion',
  'debts-aging',
  'patient-sources',
  'lab',
  'overdue-lab-orders',
] as const;
export type ExportableReport = (typeof EXPORTABLE_REPORTS)[number];

/**
 * Подписи для файла выгрузки. Аналитика хранит только идентификаторы; имена
 * врачей и номера нарядов подставляет шлюз `core-api` — у той стороны, где
 * есть и данные, и право пользователя на них. Ключ — идентификатор из
 * read-модели, значение — то, что писать в ячейку вместо него.
 */
export const ExportLabels = z.object({
  doctors: z.record(z.string(), z.string()).optional(),
  labOrders: z.record(z.string(), z.string()).optional(),
  /** Названия источников обращения — свои у каждой клиники, коды общие. */
  sources: z.record(z.string(), z.string()).optional(),
});
export type ExportLabels = z.infer<typeof ExportLabels>;
